import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, Table } from '@shared/components';
import { formatVerbatimMoneyCurrency } from '@shared/utils/formatMoneyDisplay';
import type { PlanCost, PlanCostAmounts } from '../../types/financials.types';
import { CARD_TABLE_CLASSES, TOTAL_ROW_CLASSES } from './cardTableClasses';
import { toRows, type CostRow } from '../../utils/planCostRows';

interface Props {
    planCost: PlanCost;
    currency: string;
}

const headingId = 'agreement-financials-plan-cost';

const COLUMN_WIDTHS = { net: 96, vat: 84, gross: 96 };

const PLAN_COST_TABLE_CLASSES = `${CARD_TABLE_CLASSES} ${TOTAL_ROW_CLASSES}`;

export function PlanCostCard({ planCost, currency }: Props) {
    const rows = useMemo(() => toRows(planCost), [planCost]);
    const columns = useMemo<ColumnDef<CostRow>[]>(() => {
        const money = (key: keyof PlanCostAmounts, header: string, muted = false): ColumnDef<CostRow> => ({
            id: key,
            header,
            cell: ({ row }) => (
                <Figure bold={row.original.isTotal} muted={muted && !row.original.isTotal}>
                    {formatVerbatimMoneyCurrency(row.original[key], currency)}
                </Figure>
            ),
        });
        return [
            {
                id: 'label',
                header: () => (
                    <>
                        <span aria-hidden="true" className="text-base font-semibold text-mp-foreground">
                            Total Plan Cost
                        </span>
                        <span className="sr-only">Cost line</span>
                    </>
                ),
                cell: ({ row }) => <Figure bold={row.original.isTotal}>{row.original.label}</Figure>,
            },
            money('net', 'Ex VAT'),
            money('vat', 'VAT', true),
            money('gross', 'Inc VAT'),
        ];
    }, [currency]);

    return (
        <Card className="h-full">
            <CardContent className="py-5">
                <section aria-labelledby={headingId}>
                    <h3 id={headingId} className="sr-only">
                        Total Plan Cost
                    </h3>
                    <Table<CostRow>
                        data={rows}
                        columns={columns}
                        enableSorting={false}
                        getRowId={(entry) => entry.id}
                        columnWidths={COLUMN_WIDTHS}
                        className={PLAN_COST_TABLE_CLASSES}
                    />
                </section>
            </CardContent>
        </Card>
    );
}

function Figure({ bold, muted = false, children }: { bold: boolean; muted?: boolean; children: React.ReactNode }) {
    const weight = bold ? 'font-bold' : '';
    const tone = muted ? 'text-mp-muted' : '';
    return <span className={`${weight} ${tone}`.trim() || undefined}>{children}</span>;
}
